import axios from 'axios';
import { fetchTableItems } from '.';
import {
	EventListType,
	EventStatusesInListType,
	PeriodConstraints,
} from '../../store/types';
import { Event, EventSchedule } from '../../types/event';
import { PaymentTypeDto, TableItemsOptions } from '../../types';
import {
	EventApplicationDto,
	EventAdmissionType,
	EventApplication,
	EventApplicationUpdateDto,
	EventAttendance,
	EventDto,
	EventProgress,
	EventQrCodeCheckIn,
	EventScheduleInstance,
	EventTicketItem,
	ParticipatedEvent,
	QrCodeMap,
	EventCheckIn,
	EventValidations,
	EventPaymentType,
} from '../../types/event';

export const eventApi = {
	async fetchEvents({
		ids,
		options,
		listType,
		progress,
		admissionType,
		reportId,
		periodConstraints,
		centerIds,
	}: {
		ids?: string[];
		options: TableItemsOptions;
		listType?: EventListType;
		progress?: EventProgress | EventProgress[];
		admissionType?: EventAdmissionType | EventAdmissionType[];
		reportId?: string;
		periodConstraints?: PeriodConstraints;
		centerIds?: number[];
	}): Promise<any> {
		const baseUrl = '/event/events';
		const params = new URLSearchParams();

		if (listType) {
			EventStatusesInListType[listType].forEach(s => {
				params.append('statuses', s);
			});
		}

		if (ids) {
			ids.forEach(id => {
				params.append('ids', id);
			});
		}

		if (progress) {
			if (typeof progress === 'string') {
				params.set('progresses', progress);
			} else {
				progress.forEach(p => {
					params.append('progresses', p);
				});
			}
		}

		if (admissionType) {
			if (typeof admissionType === 'string') {
				params.set('admissionTypes', admissionType);
			} else {
				admissionType.forEach(at => {
					params.append('admissionTypes', at);
				});
			}
		}

		if (
			listType &&
			[EventListType.attendance, EventListType.ticketing].includes(listType)
		) {
			params.append('attendance', 'true');
		}

		if (centerIds) {
			centerIds.forEach(id => {
				params.append('centerIds', id.toString());
			});
		}

		if (reportId) {
			params.append('reportId', reportId);
		}

		if (periodConstraints) {
			params.append('periodInclusive', periodConstraints.inclusive.toString());
			if (periodConstraints.start) {
				params.append('periodStart', periodConstraints.start.toString());
			}
			if (periodConstraints.end) {
				params.append('periodEnd', periodConstraints.end.toString());
			}
		}

		// build URL
		const data = await fetchTableItems({
			...options,
			url: `${baseUrl}?${params.toString()}`,
		});
		return data;
	},

	async fetchEventById(id: string) {
		const { data } = await axios.get(`/event/events/${id}`);
		return data;
	},

	async fetchEventByIds(body: { ids: string[] }): Promise<Event[]> {
		const { data } = await axios.post(`/event/events/get-filtered`, body);
		return data;
	},

	async fetchActivityThemes() {
		const { data } = await axios.get('/event/activity-themes');
		return data;
	},

	async fetchActivityTypes() {
		const { data } = await axios.get('/event/activity-types');
		return data;
	},

	async fetchServiceTypes() {
		const { data } = await axios.get('/event/service-types');
		return data;
	},

	async fetchTargetAudiences() {
		const { data } = await axios.get('/event/target-audiences');
		return data;
	},

	async createEvent(event: EventDto, validations: EventValidations) {
		const { data } = await axios.post('/event/events', {
			...event,
			validations,
		});
		return data;
	},

	async updateEvent(event: EventDto, validations: EventValidations) {
		if (!event.id) throw new Error('Missing event.id');
		const { data } = await axios.put(`/event/events/${event.id}`, {
			...event,
			validations,
		});
		return data;
	},

	async appendSchedules({
		eventId,
		optionId,
		schedules,
	}: {
		eventId: string;
		optionId: string;
		schedules: EventSchedule[];
	}): Promise<{ failed?: string[]; succeeded: string[] }> {
		const { data } = await axios.post(
			`/event/events/${eventId}/options/${optionId}/schedules`,
			{
				schedules,
			},
		);
		return data;
	},

	async createEventApplication(
		application: EventApplicationDto,
	): Promise<EventApplication> {
		const { data } = await axios.post(
			`/volunteer/ngo-event-applications`,
			application,
		);
		return data;
	},

	async updateEventApplication(
		applicationId: string,
		applicationDto: Partial<EventApplicationUpdateDto>,
	): Promise<EventApplication> {
		const { data } = await axios.put(
			`/volunteer/ngo-event-applications/${applicationId}`,
			applicationDto,
		);
		return data;
	},

	async fetchEventApplicationById(id: string): Promise<any> {
		const { data } = await axios.get(`/volunteer/ngo-event-applications/${id}`);
		return data;
	},

	async fetchEventApplicationsByUserId({
		userId,
	}: {
		userId: string;
	}): Promise<EventApplication[]> {
		const { data } = await axios.get(`/volunteer/ngo-event-applications`, {
			params: { userIds: [userId] },
		});
		return data;
	},

	async fetchEventApplicationByEventIdAndUserIds({
		eventId,
		userIds,
		excludeCancelled = true,
	}: {
		eventId: string;
		userIds: string[];
		excludeCancelled?: boolean;
	}): Promise<EventApplication[]> {
		const { data } = await axios.get(`/volunteer/ngo-event-applications`, {
			params: { eventId, userIds, excludeCancelled },
		});
		return data;
	},

	async fetchEventApplications(
		options: TableItemsOptions,
		eventId: string,
	): Promise<any> {
		const data = await fetchTableItems({
			...options,
			url: `event/events/${eventId}/applications`,
		});
		return data;
	},

	async fetchEventScheduleInstances(
		options: TableItemsOptions,
		eventId: string,
		optionId: string,
	): Promise<{
		result: EventScheduleInstance[];
		total: number;
	}> {
		const baseUrl = `/event/events/${eventId}/schedule-instances`;
		const params = new URLSearchParams();
		params.set('optionId', optionId);
		const { result, total } = await fetchTableItems({
			...options,
			url: `${baseUrl}?${params}`,
		});
		return { result, total };
	},

	async fetchEventScheduleInstance(id: string): Promise<EventScheduleInstance> {
		const { data } = await axios.get(`/event/schedule-instances/${id}`);
		return data;
	},

	async fetchAttendanceList(
		options: TableItemsOptions,
		scheduleInstanceId: string,
	): Promise<{
		result: EventAttendance[];
		total: number;
	}> {
		const { result, total } = await fetchTableItems({
			...options,
			url: `volunteer/ngo-event-attendances?scheduleInstanceId=${scheduleInstanceId}`,
		});
		return { result, total };
	},

	async fetchEventAttendances(params: {
		scheduleInstanceId?: string;
		eventId?: string;
		fullList?: boolean;
		userId?: string;
	}): Promise<EventAttendance[]> {
		const { data } = await axios.get(`volunteer/ngo-event-attendances`, {
			params,
		});
		return data;
	},

	async saveEventAttendance(
		changes: EventCheckIn[],
	): Promise<{ failed?: string[]; succeeded: string[] }> {
		try {
			const { data } = await axios.put(`/volunteer/ngo-job-attendances/bulk`, {
				changes,
			});
			return data;
		} catch {
			// assume all failed
			return { succeeded: [] };
		}
	},

	async checkin(attendanceId: string): Promise<EventAttendance> {
		const { data } = await axios.post(
			`/volunteer/ngo-event-attendances/checkin`,
			{
				attendanceId,
			},
		);
		return data;
	},

	async unconfirm(attendanceId: string): Promise<EventAttendance> {
		const { data } = await axios.post(
			`/volunteer/ngo-event-attendances/unconfirm`,
			{
				attendanceId,
			},
		);
		return data;
	},

	async attendanceBulkCheckIn(scheduleInstanceIds: string[]) {
		const data = await axios.post(
			`/volunteer/ngo-event-attendances/bulk/checkin`,
			{
				ids: scheduleInstanceIds,
			},
		);
		return data.data;
	},

	async updateAttendanceRemarks(
		admissionType: EventAdmissionType,
		attendanceId: string,
		remarks: string,
	): Promise<EventAttendance> {
		const domain =
			admissionType === EventAdmissionType.checkIn
				? 'ngo-event-attendances'
				: 'ngo-event-ticket-items';
		const { data } = await axios.post(`/volunteer/${domain}/update-remarks`, {
			attendanceId,
			remarks,
		});
		return data;
	},

	async attendanceAbsent(
		attendanceId: string,
		absentReason: string,
	): Promise<EventAttendance> {
		const { data } = await axios.post(
			`/volunteer/ngo-event-attendances/absent`,
			{
				attendanceId,
				absentReason,
			},
		);
		return data;
	},

	async fetchEventTicketsByOptionId(
		options: TableItemsOptions,
		optionId: string,
	): Promise<any> {
		const baseUrl = `volunteer/ngo-event-ticket-items`;
		const params = new URLSearchParams();
		params.set('optionId', optionId);
		const data = await fetchTableItems({
			...options,
			url: `${baseUrl}?${params}`,
		});
		return data;
	},

	async redeemTicket(ticketId: string): Promise<EventTicketItem> {
		const data = await axios.post(
			`volunteer/ngo-event-ticket-items/${ticketId}/redeem`,
		);
		return data.data;
	},

	async undoRedeemTicket(ticketId: string): Promise<EventTicketItem> {
		const data = await axios.post(
			`volunteer/ngo-event-ticket-items/${ticketId}/undo-redeem`,
		);
		return data.data;
	},

	async redeemTicketByQrCode(qrCode: string): Promise<EventTicketItem> {
		const data = await axios.post(
			`volunteer/ngo-event-ticket-items/qr-code-redeem`,
			{
				qrCode,
			},
		);
		return data.data;
	},

	async ticketBulkRedeem(ticketIds: string[]): Promise<any> {
		const data = await axios.post(
			`/volunteer/ngo-event-ticket-items/bulk-redeem`,
			{
				ids: ticketIds,
			},
		);
		return data.data;
	},

	async fetchParticipatedEvents(
		appUserId: string,
	): Promise<ParticipatedEvent[]> {
		const data = await axios.get(
			`/volunteer/ngo-participants/${appUserId}/events`,
		);
		return data.data;
	},

	async fetchAttendanceQrCodes(
		scheduleInstanceId: string,
	): Promise<QrCodeMap[]> {
		const { data } = await axios.get(
			`/volunteer/ngo-event-attendances/qr-code-map`,
			{
				params: { scheduleInstanceId },
			},
		);
		return data;
	},

	async fetchAttendanceContactQrCodes(
		scheduleInstanceId: string,
	): Promise<QrCodeMap[]> {
		const {
			data,
		} = await axios.get(
			`/volunteer/ngo-event-attendances/contact-qr-code-map`,
			{ params: { scheduleInstanceId } },
		);
		return data;
	},

	async saveRecordedCheckin(
		checkins: EventQrCodeCheckIn[],
	): Promise<{
		succeeded: any[];
		failed: any[];
	}> {
		const data = await axios.post(
			`/volunteer/ngo-event-attendances/bulk/save-recorded-checkin`,
			{ checkins },
		);
		return data.data;
	},

	async applyEventOnBehalf(
		userId: string,
		applyEventData: EventApplicationDto,
	): Promise<EventApplication> {
		const { data } = await axios.post(`/volunteer/ngo-event-applications`, {
			userId,
			...applyEventData,
		});
		return data;
	},

	async createPaymentType(
		paymentType: PaymentTypeDto,
	): Promise<EventPaymentType> {
		const { data } = await axios.post('/event/payment-types', paymentType);
		return data;
	},

	async fetchEventAttendanceListForExport(body: {
		eventId: string;
		scheduleInstanceId?: string;
		dateFormat: string;
		timeFormat: string;
	}) {
		const { data } = await axios.post(
			'volunteer/ngo-event-attendances/export',
			{
				eventId: body.eventId,
				scheduleInstanceId: body.scheduleInstanceId,
				dateFormat: body.dateFormat,
				timeFormat: body.timeFormat,
			},
		);
		return data;
	},

	async fetchEventTicketItemsForExport(body: {
		eventId: string;
		dateFormat: string;
		timeFormat: string;
	}) {
		const { data } = await axios.post(
			'volunteer/ngo-event-ticket-items/export',
			{
				eventId: body.eventId,
				dateFormat: body.dateFormat,
				timeFormat: body.timeFormat,
			},
		);
		return data;
	},

	async fetchEventApplicationForExport(body: {
		eventId: string;
		dateFormat: string;
		timeFormat: string;
	}) {
		const { data } = await axios.post(
			'volunteer/ngo-event-applications/export',
			{
				eventId: body.eventId,
				dateFormat: body.dateFormat,
				timeFormat: body.timeFormat,
			},
		);
		return data;
	},
};
